/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import { css } from '@emotion/react';
import classNames from 'classnames';
import { keysOf } from '../../common';
var typeToClassNameMap = {
  inList: 'euiCheckbox--inList'
};
export var TYPES = keysOf(typeToClassNameMap);
export class EuiCheckbox extends Component {
  static defaultProps = {
    checked: false,
    disabled: false,
    indeterminate: false,
    compressed: false
  };
  inputRef = undefined;
  componentDidMount() {
    this.invalidateIndeterminate();
  }
  componentDidUpdate() {
    this.invalidateIndeterminate();
  }
  render() {
    const {
      className,
      css: customCss,
      id,
      checked,
      label,
      onChange,
      type,
      disabled,
      compressed,
      indeterminate,
      inputRef,
      labelProps,
      ...rest
    } = this.props;
    const classes = classNames('euiCheckbox', type && typeToClassNameMap[type], {
      'euiCheckbox--noLabel': !label,
      'euiCheckbox--compressed': compressed
    }, className);
    const labelClasses = classNames('euiCheckbox__label', labelProps?.className);
    let optionalLabel;
    if (label) {
      optionalLabel = <label {...labelProps} className={labelClasses} htmlFor={id}>
          {label}
        </label>;
    }
    const styles = {
      euiCheckbox: /*#__PURE__*/css(";label:euiCheckbox;")
    }; // TODO: Emotion conversion
    const cssStyles = [styles.euiCheckbox, customCss];
    return <div css={cssStyles} className={classes}>
        <input className="euiCheckbox__input" type="checkbox" id={id} checked={checked} onChange={onChange} disabled={disabled} ref={this.setInputRef} {...rest} />

        <div className="euiCheckbox__square" />

        {optionalLabel}
      </div>;
  }
  setInputRef = input => {
    this.inputRef = input;
    if (this.props.inputRef) {
      this.props.inputRef(input);
    }
    this.invalidateIndeterminate();
  };
  invalidateIndeterminate() {
    if (this.inputRef) {
      this.inputRef.indeterminate = this.props.indeterminate;
    }
  }
}
EuiCheckbox.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.any.isRequired,
  // overriding to make it required
  inputRef: PropTypes.func,
  label: PropTypes.node,
  type: PropTypes.oneOf(["inList"]),
  disabled: PropTypes.bool,
  /**
     * when `true` creates a shorter height checkbox row
     */
  compressed: PropTypes.bool,
  indeterminate: PropTypes.bool,
  /**
     * Object of props passed to the <label/>
     */
  labelProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
try {
  EuiCheckbox.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/form/checkbox/checkbox.tsx',
    description: '',
    displayName: 'EuiCheckbox',
    methods: [],
    props: {
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      checked: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'checked',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: true,
        type: {
          name: 'ChangeEventHandler<HTMLInputElement>'
        }
      },
      inputRef: {
        defaultValue: null,
        description: '',
        name: 'inputRef',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: false,
        type: {
          name: '(element: HTMLInputElement) => void'
        }
      },
      label: {
        defaultValue: null,
        description: '',
        name: 'label',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      type: {
        defaultValue: null,
        description: '',
        name: 'type',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: false,
        type: {
          name: '"inList"'
        }
      },
      disabled: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'disabled',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: {
          value: 'false'
        },
        description: 'when `true` creates a shorter height checkbox row',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      indeterminate: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'indeterminate',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      labelProps: {
        defaultValue: null,
        description: 'Object of props passed to the <label/>',
        name: 'labelProps',
        parent: {
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/checkbox/checkbox.tsx',
          name: 'EuiCheckboxProps'
        }],
        required: false,
        type: {
          name: 'CommonProps & LabelHTMLAttributes<HTMLLabelElement>'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      }
    },
    extendedInterfaces: ['EuiCheckboxProps', 'CommonProps', 'InputHTMLAttributes', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}